import { TransactionAPI, AccountAPI } from "@/apis/accounting"
import { BillAPI } from "@/apis/purchasing"
import { InvoiceAPI } from "@/apis/sales"
import { Cascader } from "@arco-design/web-react"
import _ from "lodash";
import { useCallback, useEffect, useState } from "react"
import { Currency } from "@/components/Numeric";
import { TransactionCategoryDef, AccountCategoryDef, AccountTypeDef } from "@/enums/accounting";
import { buildEnumOptions } from "@/enums/utils";

const INCOME_CATEGORY_VALUES = [
    TransactionCategoryDef.InvoicePaymentReceived.value,
    TransactionCategoryDef.TransferFrom.value,
    TransactionCategoryDef.BillRefundReceived.value,
    TransactionCategoryDef.ExpenseRefundReceived.value,
    TransactionCategoryDef.IncomeAccount.value,
];

const EXPENSE_CATEGORY_VALUES = [
    TransactionCategoryDef.BillPaymentSent.value,
    TransactionCategoryDef.TransferTo.value,
    TransactionCategoryDef.PaymentToShareholder.value,
    TransactionCategoryDef.InvoiceRefundSent.value,
    TransactionCategoryDef.IncomeRefundSent.value,
    TransactionCategoryDef.ExpenseAccount.value,
];

const BANK_CC_LOAN_ACCOUNT_TYPES = [
    AccountTypeDef.CashAndBank.value,
    AccountTypeDef.CreditCard.value,
];

const toInvoiceOption = (o) => ({
    value: o.id,
    label: <span>${o.invoiceNo}(<Currency value={o.totalAmount} />)</span>
});

const toBillOption = (o) => ({
    value: o.id,
    label: <span>${o.billNo}(<Currency value={o.totalAmount} />)</span>
});

const toAccountOption = (o) => ({
    value: o.id,
    label: o.accountName
});

const toRelatedTransOption = (o) => ({
    value: `${o.id}-1`,
    label: `${o.date} ${o.description}`
});

const assignChildren = (categories, categoryValue, children) => {
    const category = _.find(categories, { value: categoryValue });
    if (category) {
        category.children = children;
    }
};

const TransactionCategorySelect = (props) => {
    const { value, placeholder, type, amount, transactionId, ...restProps } = props;
    const [options, setOptions] = useState([]);
    const [current, setCurrent] = useState([]);

    const getAccounts = useCallback(async () => {
        return await AccountAPI.actives({});
    }, []);

    const getInvoices = useCallback(async () => {
        return await InvoiceAPI.actives();
    }, []);

    const getBills = useCallback(async () => {
        return await BillAPI.actives();
    }, []);

    const getRelatedTrans = useCallback(async () => {
        if (transactionId) {
            return await TransactionAPI.relatedTransactions(transactionId);
        }
        return [];
    }, [transactionId])

    const onValueChange = (val) => {
        if (!val || val.length <= 1) {
            props.onChange && props.onChange();
            return;
        }
        let categoryValue = _.split(val[1], '-');
        props.onChange && props.onChange({
            category: val[0],
            categoryValue: parseInt(categoryValue[0]),
            categoryValueType: categoryValue[1] ? parseInt(categoryValue[1]) : null,
        });
    }

    const getOptions = useCallback(async (type) => {
        let categories = buildEnumOptions(TransactionCategoryDef);
        const accounts = await getAccounts();
        const invoices = await getInvoices();
        const bills = await getBills();
        const relatedTransactions = await getRelatedTrans();

        const sortedInvoices = _.orderBy(invoices, ['id'], ['desc']);
        const sortedBills = _.orderBy(bills, ['id'], ['desc']);
        const bankCCLoanAccounts = _.orderBy(
            _.filter(accounts, o => _.includes(BANK_CC_LOAN_ACCOUNT_TYPES, o.accountType)),
            ['accountName'], ['asc']
        );
        const expenseAccounts = _.orderBy(
            _.filter(accounts, { category: AccountCategoryDef.Expenses.value }),
            ['accountName'], ['asc']
        );
        const incomeAccounts = _.orderBy(
            _.filter(accounts, { category: AccountCategoryDef.Income.value }),
            ['accountName'], ['asc']
        );
        const equityAccounts = _.orderBy(
            _.filter(accounts, account => account.category === AccountCategoryDef.Equity.value && account.accountName !== "Retained Income"),
            ['accountName'], ['asc']
        );

        let options = [];
        if (type === "income" || type === null) {
            let incomeCategories = _.filter(categories, c => _.includes(INCOME_CATEGORY_VALUES, c.value));
            assignChildren(incomeCategories, TransactionCategoryDef.InvoicePaymentReceived.value, _.map(sortedInvoices, toInvoiceOption));
            assignChildren(incomeCategories, TransactionCategoryDef.TransferFrom.value,
                _.concat(_.map(relatedTransactions, toRelatedTransOption), _.map(_.concat(bankCCLoanAccounts, equityAccounts), toAccountOption))
            );
            assignChildren(incomeCategories, TransactionCategoryDef.BillRefundReceived.value, _.map(sortedBills, toBillOption));
            assignChildren(incomeCategories, TransactionCategoryDef.ExpenseRefundReceived.value, _.map(expenseAccounts, toAccountOption));
            assignChildren(incomeCategories, TransactionCategoryDef.IncomeAccount.value, _.map(incomeAccounts, toAccountOption));
            options = _.concat(options, incomeCategories);
        }

        if (type === "expense" || type === null) {
            let expenseCategories = _.filter(categories, c => _.includes(EXPENSE_CATEGORY_VALUES, c.value));
            assignChildren(expenseCategories, TransactionCategoryDef.BillPaymentSent.value, _.map(sortedBills, toBillOption));
            assignChildren(expenseCategories, TransactionCategoryDef.TransferTo.value,
                _.concat(_.map(relatedTransactions, toRelatedTransOption), _.map(bankCCLoanAccounts, toAccountOption))
            );
            assignChildren(expenseCategories, TransactionCategoryDef.PaymentToShareholder.value, _.map(equityAccounts, toAccountOption));
            assignChildren(expenseCategories, TransactionCategoryDef.InvoiceRefundSent.value, _.map(sortedInvoices, toInvoiceOption));
            assignChildren(expenseCategories, TransactionCategoryDef.IncomeRefundSent.value, _.map(incomeAccounts, toAccountOption));
            assignChildren(expenseCategories, TransactionCategoryDef.ExpenseAccount.value, _.map(expenseAccounts, toAccountOption));
            options = _.concat(options, expenseCategories);
        }

        setOptions(options);
    }, [getAccounts, getInvoices, getBills, getRelatedTrans]);

    useEffect(() => {
        getOptions(type);
    }, [getOptions, type])

    useEffect(() => {
        const c = [value?.category, _.join(_.compact([value?.categoryValue, value?.valueType]), '-')];
        setCurrent(c);
    }, [value])

    return (
        <Cascader {...restProps}
            value={current}
            options={options}
            filterOption={(input, node) => {
                const keyword = _.toLower(input);
                return _.toLower(node.value.toString()).indexOf(keyword) > -1 || _.toLower(node.label).indexOf(keyword) > -1;
            }}
            showSearch
            onChange={(val) => onValueChange(val)}
            renderFormat={(valueShow) => {
                if (valueShow.length <= 0)
                    return placeholder;
                return valueShow[valueShow.length - 1];
            }}
        />
    )
}

export default TransactionCategorySelect;